/**
 * CrmProposalsList - Listagem de Propostas Geradas (Fase 2)
 *
 * Espelha integralmente a arquitetura da listagem de Contratos Gerados
 * (CrmGeneratedContracts.tsx):
 *   - header compacto premium + filtros rápidos;
 *   - cards de KPI clicáveis (filtro por situação);
 *   - tabela premium com ordenação server-side;
 *   - paginação server-side via RPC `get_proposals_paginated`;
 *   - filtros avançados em Sheet lateral (padrão do projeto);
 *   - alternador Lista/Kanban compartilhando os mesmos filtros.
 *
 * Permissões: rota protegida por `crm_proposals_list`.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 2 — Listagem de Propostas)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  FileText,
  LayoutGrid,
  List,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { startOfDay, endOfDay, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';

import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { SortableHeader } from '@/components/layout/list-page';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { CrmWorkspaceFilter } from '@/components/crm/filters/CrmWorkspaceFilter';
import { CrmSellerFilter } from '@/components/crm/filters/CrmSellerFilter';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { useCrmProposalTypes } from '@/hooks/useCrmProposalTypes';
import {
  useProposalsPaginated,
  type PaginatedProposal,
  type ProposalSortField,
  type ProposalSortDirection,
} from '@/hooks/useProposalsPaginated';
import { useProposalListKpis } from '@/hooks/useProposalListKpis';
import { ProposalListKpiCards } from '@/components/crm/proposals/ProposalListKpiCards';
import { ProposalRowActions } from '@/components/crm/proposals/ProposalRowActions';
import type { ProposalFilters } from '@/hooks/useProposalKanbanColumn';
import {
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_STATUS_LABELS,
  type ProposalStatus,
} from '@/hooks/useDealProposals';
import { ProposalDetailsModal } from '@/components/crm/deals/modal/tabs/proposals/ProposalDetailsModal';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';

/** Campos de data disponíveis (mesma semântica do Kanban de Propostas) */
const DATE_FIELDS: { value: string; label: string }[] = [
  { value: 'created_at', label: 'Criada em' },
  { value: 'sent_at', label: 'Enviada em' },
  { value: 'accepted_at', label: 'Aceita em' },
  { value: 'expires_at', label: 'Expira em' },
  { value: 'updated_at', label: 'Atualizada em' },
];

/** Situações principais oferecidas no filtro rápido */
const MAIN_STATUSES: ProposalStatus[] = [
  'draft',
  'generated',
  'sent',
  'viewed',
  'accepted',
  'rejected',
  'expired',
  'cancelled',
  'superseded',
];

/** Calcula o intervalo de datas de um preset (mesma regra dos demais módulos) */
function resolvePreset(preset: PeriodPreset): { startDate: Date; endDate: Date } {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { startDate: startOfDay(y), endDate: endOfDay(y) };
    }
    case '7days':
      return { startDate: startOfDay(subDays(now, 7)), endDate: endOfDay(now) };
    case '15days':
      return { startDate: startOfDay(subDays(now, 15)), endDate: endOfDay(now) };
    case 'lastMonth': {
      const last = subMonths(now, 1);
      return { startDate: startOfMonth(last), endDate: endOfMonth(last) };
    }
    default:
      return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
  }
}

export default function CrmProposalsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  // ------------------------------------------------------------- Filtros
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    // Debounce local: evita uma RPC por tecla digitada
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [proposalTypeId, setProposalTypeId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateField, setDateField] = useState<string>('updated_at');
  const [period, setPeriod] = useState<PeriodFilter>({
    preset: 'month',
    ...resolvePreset('month'),
  } as PeriodFilter);

  // Navegador rápido por mês (mesmo componente da listagem de contratos)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());

  // Filtros avançados (Sheet lateral)
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [pdfStatus, setPdfStatus] = useState<string>('all');
  const [publicEnabled, setPublicEnabled] = useState<string>('all');
  const [viewed, setViewed] = useState<string>('all');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [minMrr, setMinMrr] = useState('');
  const [maxMrr, setMaxMrr] = useState('');

  // KPI ativo (filtro via cards)
  const [activeKpiStatus, setActiveKpiStatus] = useState<string | null>(null);

  // Paginação e ordenação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<ProposalSortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<ProposalSortDirection>('desc');

  // Modais
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);

  const { activeTypes } = useCrmProposalTypes();

  // ------------------------------------------------------- Recorte de datas
  // O MonthNavigator aplica o mês sobre o campo de data escolhido pelo usuário;
  // só cai em created_at quando o usuário marca "Todas datas".
  const resolvedDateField = monthFilter
    ? (dateField !== 'all' ? dateField : 'created_at')
    : (dateField !== 'all' ? dateField : null);
  const resolvedDateFrom = monthFilter
    ? startOfMonth(monthFilter).toISOString()
    : (dateField !== 'all' ? period.startDate.toISOString() : null);
  const resolvedDateTo = monthFilter
    ? endOfMonth(monthFilter).toISOString()
    : (dateField !== 'all' ? period.endDate.toISOString() : null);

  /** Filtros consolidados enviados às RPCs da listagem */
  const filters: ProposalFilters = useMemo(
    () => ({
      search: debouncedSearch || null,
      workspace_id: workspaceId,
      seller_id: sellerId,
      proposal_type_id: proposalTypeId === 'all' ? null : proposalTypeId,
      status: statusFilter === 'all' ? null : (statusFilter as ProposalStatus),
      created_by: createdBy,
      assigned_to: assignedTo,
      date_field: resolvedDateField,
      date_from: resolvedDateFrom,
      date_to: resolvedDateTo,
      pdf_status: pdfStatus === 'all' ? null : pdfStatus,
      public_enabled: publicEnabled === 'all' ? null : publicEnabled === 'yes',
      viewed: viewed === 'all' ? null : viewed === 'yes',
      min_total: minTotal ? Number(minTotal) : null,
      max_total: maxTotal ? Number(maxTotal) : null,
      min_mrr: minMrr ? Number(minMrr) : null,
      max_mrr: maxMrr ? Number(maxMrr) : null,
    }),
    [
      debouncedSearch,
      workspaceId,
      sellerId,
      proposalTypeId,
      statusFilter,
      createdBy,
      assignedTo,
      resolvedDateField,
      resolvedDateFrom,
      resolvedDateTo,
      pdfStatus,
      publicEnabled,
      viewed,
      minTotal,
      maxTotal,
      minMrr,
      maxMrr,
    ],
  );

  /**
   * Filtros dos KPIs: excluem o recorte aplicado pelo próprio card ativo
   * para evitar circularidade (mesmo padrão da listagem de contratos).
   */
  const kpiFilters: ProposalFilters = useMemo(
    () => ({
      ...filters,
      status: activeKpiStatus && activeKpiStatus !== 'created_today' ? null : filters.status,
      date_field: activeKpiStatus === 'created_today' ? null : filters.date_field,
      date_from: activeKpiStatus === 'created_today' ? null : filters.date_from,
      date_to: activeKpiStatus === 'created_today' ? null : filters.date_to,
    }),
    [filters, activeKpiStatus],
  );

  const { kpis, isLoading: kpisLoading } = useProposalListKpis(kpiFilters);

  const {
    proposals,
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = useProposalsPaginated(filters, {
    page: currentPage,
    pageSize,
    sortField,
    sortDirection,
  });

  /** Quantidade de filtros avançados ativos (badge do botão "Mais") */
  const extraFiltersCount = [
    createdBy,
    assignedTo,
    pdfStatus !== 'all' ? pdfStatus : null,
    publicEnabled !== 'all' ? publicEnabled : null,
    viewed !== 'all' ? viewed : null,
    minTotal || null,
    maxTotal || null,
    minMrr || null,
    maxMrr || null,
  ].filter(Boolean).length;

  const hasActiveFilters =
    !!search ||
    !!workspaceId ||
    !!sellerId ||
    proposalTypeId !== 'all' ||
    statusFilter !== 'all' ||
    dateField !== 'updated_at' ||
    extraFiltersCount > 0;

  /** Limpa todos os filtros de uma vez */
  const clearFilters = () => {
    setSearch('');
    setWorkspaceId(null);
    setSellerId(null);
    setProposalTypeId('all');
    setStatusFilter('all');
    setDateField('updated_at');
    setActiveKpiStatus(null);
    setCreatedBy(null);
    setAssignedTo(null);
    setPdfStatus('all');
    setPublicEnabled('all');
    setViewed('all');
    setMinTotal('');
    setMaxTotal('');
    setMinMrr('');
    setMaxMrr('');
    setMonthFilter(new Date());
    setPeriod({ preset: 'month', ...resolvePreset('month') } as PeriodFilter);
  };

  // Reset de página sempre que os filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  /** Filtro via card de KPI (toggle) */
  const handleKpiFilter = (status: string | null) => {
    if (activeKpiStatus === status) {
      setActiveKpiStatus(null);
      setStatusFilter('all');
      return;
    }

    setActiveKpiStatus(status);

    if (status === null) {
      setStatusFilter('all');
    } else if (status === 'created_today') {
      // "Criadas Hoje": limpa a situação e recorta a data de criação
      setStatusFilter('all');
      setMonthFilter(null);
      setDateField('created_at');
      const now = new Date();
      setPeriod({ preset: 'today', startDate: startOfDay(now), endDate: endOfDay(now) } as PeriodFilter);
    } else {
      setStatusFilter(status);
    }
  };

  /** Ordenação por coluna */
  const handleSort = (field: ProposalSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  /** Atualização manual dos dados */
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      await refetch();
    });
  };

  /** Invalida listagem + KPIs após qualquer ação de linha */
  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['proposals-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['proposal-list-kpis'] });
  }, [queryClient]);

  /** Classe padrão dos gatilhos de filtro (mesma da listagem de contratos) */
  const triggerClass = 'h-6 text-[11px] sm:h-8 sm:text-sm border-0 bg-background rounded-full';

  return (
    <div className="flex flex-col h-full">
      {/* Header compacto premium */}
      <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2 sm:shrink-0">
          <div>
            <h1 className="text-xl font-semibold">Propostas Geradas</h1>
          </div>

          {/* Ações mobile */}
          <div className="flex items-center gap-2 sm:hidden">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
              title="Atualizar dados"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', (isRefreshing || isFetching) && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Navegador de mês — centralizado no mobile e no desktop (mesmo posicionamento
            da listagem de Contratos Gerados, onde o bloco de ações à direita é maior) */}
        {monthFilter && (
          <div className="flex justify-center sm:flex-1 sm:justify-center">
            <MonthNavigator
              currentMonth={monthFilter}
              onChange={(d) => setMonthFilter(d)}
              onClear={() => setMonthFilter(null)}
            />
          </div>
        )}

        {/* Ações desktop */}
        <div className="hidden sm:flex items-center gap-2">
          {/* Alternador Lista / Kanban */}
          <div className="flex items-center rounded-md border p-0.5">
            <Button variant="secondary" size="icon-sm" title="Visualizar em lista">
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate('/tenant/crm/proposals/kanban')}
              title="Visualizar em kanban por situação"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isFetching}
            title="Atualizar dados"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', (isRefreshing || isFetching) && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Cards de KPI totalizadores */}
      <ProposalListKpiCards
        kpis={kpis}
        loading={kpisLoading}
        activeStatus={activeKpiStatus ?? undefined}
        onFilterByStatus={handleKpiFilter}
      />

      {/* Barra de filtros premium */}
      <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por proposta, cliente, negócio, telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
          />
        </div>

        {/* Campo de data */}
        <Select value={dateField} onValueChange={setDateField}>
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[140px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5 min-w-0">
              <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {dateField === 'all'
                  ? 'Período'
                  : DATE_FIELDS.find((d) => d.value === dateField)?.label || 'Período'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas datas</SelectItem>
            {DATE_FIELDS.map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Período (apenas quando há campo de data escolhido) */}
        {dateField !== 'all' && (
          <CrmPeriodFilter
            value={period}
            onPresetChange={(preset) => setPeriod({ preset, ...resolvePreset(preset) } as PeriodFilter)}
            onCustomChange={(startDate, endDate) =>
              setPeriod({ preset: 'custom', startDate, endDate } as PeriodFilter)
            }
            className={triggerClass}
          />
        )}

        {/* Workspace */}
        <CrmWorkspaceFilter value={workspaceId} onChange={setWorkspaceId} className={triggerClass} />

        {/* Situação */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={cn('w-[95px] sm:w-[150px]', triggerClass)}>
            <SelectValue placeholder="Situação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas situações</SelectItem>
            {MAIN_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {PROPOSAL_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Vendedor */}
        <CrmSellerFilter value={sellerId} onChange={setSellerId} className={triggerClass} />

        {/* Tipo da proposta */}
        <Select value={proposalTypeId} onValueChange={setProposalTypeId}>
          <SelectTrigger
            className={cn('w-[95px] sm:w-[150px]', triggerClass, proposalTypeId !== 'all' && 'border-primary bg-primary/5')}
          >
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {activeTypes.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtros avançados */}
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 shrink-0 h-6 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs"
          onClick={() => setIsMoreOpen(true)}
        >
          <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          Mais
          {extraFiltersCount > 0 && (
            <Badge
              variant="secondary"
              className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0"
            >
              {extraFiltersCount}
            </Badge>
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-muted-foreground hover:text-foreground shrink-0 h-8 px-2 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
        )}
      </div>

      {/* Sheet de filtros avançados (mesmo conteúdo do Kanban de Propostas) */}
      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
            <SheetDescription>Refine a busca das propostas geradas</SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Criada por</Label>
              <CrmUserFilter
                value={createdBy}
                onChange={setCreatedBy}
                label="Criada por"
                searchPlaceholder="Buscar usuário..."
                allLabel="Todos os usuários"
                groupLabel="Usuários"
                className="h-8 text-sm w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Responsável pelo negócio</Label>
              <CrmUserFilter
                value={assignedTo}
                onChange={setAssignedTo}
                label="Responsável"
                searchPlaceholder="Buscar usuário..."
                allLabel="Todos os responsáveis"
                groupLabel="Usuários"
                className="h-8 text-sm w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Situação do PDF</Label>
              <Select value={pdfStatus} onValueChange={setPdfStatus}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="queued">Na fila</SelectItem>
                  <SelectItem value="generated">Gerado</SelectItem>
                  <SelectItem value="error">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Link público</Label>
              <Select value={publicEnabled} onValueChange={setPublicEnabled}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Com link ativo</SelectItem>
                  <SelectItem value="no">Sem link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Visualização pelo cliente</Label>
              <Select value={viewed} onValueChange={setViewed}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="yes">Visualizadas</SelectItem>
                  <SelectItem value="no">Não visualizadas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Valor mínimo</Label>
                <Input
                  type="number"
                  value={minTotal}
                  onChange={(e) => setMinTotal(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valor máximo</Label>
                <Input
                  type="number"
                  value={maxTotal}
                  onChange={(e) => setMaxTotal(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">MRR mínimo</Label>
                <Input
                  type="number"
                  value={minMrr}
                  onChange={(e) => setMinMrr(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">MRR máximo</Label>
                <Input
                  type="number"
                  value={maxMrr}
                  onChange={(e) => setMaxMrr(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0,00"
                />
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1" />
              Limpar filtros
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Tabela premium com scroll horizontal indicado */}
      <ScrollIndicators className="flex-1">
        <div className="h-full w-full overflow-hidden">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : proposals.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <FileText className="h-12 w-12 text-muted-foreground/40" />
              <div className="text-center">
                <h3 className="font-medium text-sm">Nenhuma proposta encontrada</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {hasActiveFilters
                    ? 'Tente ajustar os filtros de busca'
                    : 'Nenhuma proposta foi gerada ainda'}
                </p>
              </div>
            </div>
          ) : (
            <Table scrollViewport containerClassName="h-full" className="min-w-[1700px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* Barra colorida de situação */}
                  <TableHead className="w-2 p-0" />
                  <SortableHeader label="Nº" field="proposal_number" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Proposta / Negócio" field="title" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Cliente" field="contact_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <TableHead className="text-xs font-medium">Tipo</TableHead>
                  <SortableHeader label="Valor" field="total" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="MRR" field="mrr_total" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Situação" field="status" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <TableHead className="text-xs font-medium">Itens</TableHead>
                  <SortableHeader label="Criada" field="created_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Atualizada" field="updated_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Enviada" field="sent_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Expira" field="expires_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Vendedor" field="seller_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <TableHead className="text-xs font-medium">Criada por</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposals.map((proposal: PaginatedProposal) => {
                  const statusColor = PROPOSAL_STATUS_COLORS[proposal.status] || 'bg-muted-foreground';
                  const statusLabel = PROPOSAL_STATUS_LABELS[proposal.status] || proposal.status;

                  return (
                    <TableRow
                      key={proposal.id}
                      className="cursor-pointer hover:bg-muted/40 group"
                      onClick={() => setDetailsId(proposal.id)}
                    >
                      {/* Barra lateral colorida pela situação */}
                      <TableCell className="p-0 w-2">
                        <div className={cn('w-1 h-full min-h-[40px] rounded-full', statusColor)} />
                      </TableCell>

                      {/* Número + versão */}
                      <TableCell className="py-2">
                        <span className="text-xs font-mono text-muted-foreground">
                          {proposal.proposal_number}
                        </span>
                        {proposal.version_number > 1 && (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            v{proposal.version_number}
                          </span>
                        )}
                      </TableCell>

                      {/* Título + negócio */}
                      <TableCell className="py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate max-w-[180px]">
                            {proposal.title || 'Proposta'}
                          </p>
                          {proposal.deal_title && (
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {proposal.deal_title}
                            </p>
                          )}
                        </div>
                      </TableCell>

                      {/* Cliente */}
                      <TableCell className="py-2">
                        {proposal.contact_name ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <SmartAvatar
                              src={proposal.contact_avatar}
                              name={proposal.contact_name}
                              size="xs"
                            />
                            <span className="text-sm truncate max-w-[120px]">{proposal.contact_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Tipo da proposta */}
                      <TableCell className="py-2">
                        {proposal.proposal_type_name ? (
                          <div className="flex items-center gap-1.5">
                            {proposal.proposal_type_color && (
                              <span
                                className="h-2 w-2 shrink-0 rounded-full"
                                style={{ backgroundColor: proposal.proposal_type_color }}
                              />
                            )}
                            <span className="text-xs truncate max-w-[110px]">
                              {proposal.proposal_type_name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Valor total */}
                      <TableCell className="py-2">
                        <span className="text-sm font-mono font-medium">
                          <PriceDisplay amount={proposal.total} currency={proposal.currency || 'BRL'} size="sm" />
                        </span>
                      </TableCell>

                      {/* MRR */}
                      <TableCell className="py-2">
                        {proposal.mrr_total ? (
                          <span className="text-sm font-mono">
                            <PriceDisplay amount={proposal.mrr_total} currency={proposal.currency || 'BRL'} size="sm" />
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Situação (bolinha + rótulo) */}
                      <TableCell className="py-2">
                        <div className="flex items-center gap-1.5">
                          <span className={cn('h-2 w-2 shrink-0 rounded-full', statusColor)} />
                          <span className="text-xs text-foreground whitespace-nowrap">{statusLabel}</span>
                        </div>
                      </TableCell>

                      {/* Quantidade de itens */}
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground">{proposal.items_count}</span>
                      </TableCell>

                      {/* Criada em */}
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(proposal.created_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      </TableCell>

                      {/* Atualizada em */}
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(proposal.updated_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      </TableCell>

                      {/* Enviada em */}
                      <TableCell className="py-2">
                        {proposal.sent_at ? (
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(proposal.sent_at), 'dd/MM/yy', { locale: ptBR })}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Expira em (destaca quando vencida) */}
                      <TableCell className="py-2">
                        {proposal.expires_at ? (
                          <span
                            className={cn(
                              'text-xs',
                              proposal.is_expired ? 'text-destructive' : 'text-muted-foreground',
                            )}
                          >
                            {format(new Date(proposal.expires_at), 'dd/MM/yy', { locale: ptBR })}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Vendedor */}
                      <TableCell className="py-2">
                        {proposal.seller_name ? (
                          <div className="flex items-center gap-2">
                            <SmartAvatar
                              src={proposal.seller_avatar}
                              name={proposal.seller_name}
                              size="xs"
                            />
                            <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                              {proposal.seller_name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Criada por */}
                      <TableCell className="py-2">
                        {proposal.created_by_name ? (
                          <span className="text-xs text-muted-foreground truncate max-w-[100px] inline-block">
                            {proposal.created_by_name}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">-</span>
                        )}
                      </TableCell>

                      {/* Ações contextuais (mesmas do card do Kanban) */}
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        <ProposalRowActions
                          proposal={proposal}
                          onViewDetails={() => setDetailsId(proposal.id)}
                          onViewDeal={proposal.deal_id ? () => setDealId(proposal.deal_id) : undefined}
                          onActionComplete={invalidateAll}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </ScrollIndicators>

      {/* Rodapé de paginação */}
      {!isLoading && proposals.length > 0 && (
        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Modal de detalhes da proposta */}
      <ProposalDetailsModal
        proposalId={detailsId}
        onClose={() => {
          setDetailsId(null);
          invalidateAll();
        }}
        /* Atalho do cabeçalho: fecha a proposta e abre o negócio vinculado */
        onOpenDeal={(id) => {
          setDetailsId(null);
          invalidateAll();
          setDealId(id);
        }}
      />

      {/* Modal do negócio vinculado */}
      <DealDetailsModalPremium
        dealId={dealId}
        open={!!dealId}
        onOpenChange={(open) => !open && setDealId(null)}
      />
    </div>
  );
}
